import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StyleClassModule } from 'primeng/styleclass';
import { PopoverModule } from 'primeng/popover';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ButtonModule } from 'primeng/button';
import { Observable } from 'rxjs';

import { LayoutService } from '../service/layout.service';
import { AuthService, CurrentUser } from '@/core/services/auth-service';
import { NotificationBellComponent } from '@/shared/notification-bell';
import { BrandingService } from '@/services/branding.service';
import { PanelTheme } from '@/model/availability.model';
import { pageInfoVisible, setPageInfoVisible } from '@/shared/page-info';
import { LocationsService } from '@/services/locations.service';
import { LiveRefreshService } from '@/services/live-refresh.service';

const clockFormatter = new Intl.DateTimeFormat('fa-IR', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
const dateFormatter = new Intl.DateTimeFormat('fa-IR-u-ca-persian', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule, StyleClassModule, PopoverModule, SelectButtonModule, ToggleSwitchModule, ButtonModule, NotificationBellComponent],
  styles: [
    `
      .topbar-brand-logo {
        height: 2.5rem;
        width: auto;
        max-width: 8rem;
        object-fit: contain;
      }
      .topbar-brand-name {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 40vw;
      }
      .topbar-clock {
        display: flex;
        align-items: center;
        gap: 0.9rem;
        padding: 0 0.75rem;
        white-space: nowrap;
        font-weight: 600;
      }
      .topbar-clock .time {
        font-variant-numeric: tabular-nums;
        font-size: 1.05rem;
      }
      .topbar-clock .date {
        color: var(--text-color-secondary);
        font-size: 0.92rem;
      }
      .user-trigger {
        display: inline-flex;
        align-items: center;
        gap: 0.45rem;
        height: 2.6rem;
        padding: 0 0.75rem;
        border-radius: 999px;
        border: 1px solid var(--surface-border);
        background: var(--surface-card);
        color: var(--text-color);
        cursor: pointer;
        font-family: inherit;
        white-space: nowrap;
        max-width: 60vw;
      }
      .user-trigger:hover {
        border-color: var(--primary-color);
      }
      .loc-filter {
        height: 2.4rem;
        border-radius: 999px;
        border: 1px solid var(--surface-border);
        background: var(--surface-card);
        color: var(--text-color);
        padding: 0 0.7rem;
        font-family: inherit;
        max-width: 9.5rem;
      }
      .user-trigger .avatar {
        width: 1.8rem;
        height: 1.8rem;
        border-radius: 50%;
        background: var(--primary-color);
        color: var(--primary-contrast-color);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        font-size: 0.85rem;
        flex-shrink: 0;
      }
      .user-trigger .name {
        overflow: hidden;
        text-overflow: ellipsis;
        font-weight: 600;
      }
      .user-trigger .role {
        color: var(--text-color-secondary);
        font-weight: 500;
      }
      .user-menu {
        width: min(19rem, 90vw);
        display: flex;
        flex-direction: column;
      }
      .user-menu-item {
        display: flex;
        align-items: center;
        gap: 0.6rem;
        padding: 0.65rem 0.6rem;
        border-radius: 8px;
        color: var(--text-color);
        background: none;
        border: none;
        cursor: pointer;
        font-family: inherit;
        font-size: 0.95rem;
        text-align: start;
        width: 100%;
      }
      .user-menu-item:hover {
        background: var(--surface-hover);
      }
      .user-menu-item i {
        width: 1.2rem;
        color: var(--text-color-secondary);
      }
      .user-menu-item.danger,
      .user-menu-item.danger i {
        color: #ef4444;
      }
      .display-prefs {
        display: flex;
        flex-direction: column;
        gap: 0.8rem;
        padding: 0.6rem;
        margin: 0.25rem 0;
        border-radius: 10px;
        background: var(--surface-ground);
      }
      .menu-sep {
        height: 1px;
        background: var(--surface-border);
        margin: 0.35rem 0;
      }
      .layout-topbar-actions {
        align-items: center;
        flex-shrink: 0;
      }
      .layout-topbar-logo-container {
        min-width: 0;
        flex: 1 1 auto;
      }
      .layout-topbar-logo {
        min-width: 0;
      }
      @media (max-width: 991px) {
        .layout-topbar {
          padding: 0 1rem;
        }
      }
      @media (max-width: 575px) {
        .layout-topbar {
          padding: 0 0.6rem;
        }
        .layout-topbar-actions {
          gap: 0.4rem;
        }
        .topbar-brand-logo {
          height: 2rem;
        }
        .topbar-brand-name {
          font-size: 1rem;
          max-width: 34vw;
        }
        .topbar-brand-name.has-logo {
          display: none;
        }
        .user-trigger .name,
        .user-trigger .role {
          display: none;
        }
        .user-trigger {
          padding: 0 0.35rem;
        }
      }
    `,
  ],
  template: `
    <div class="layout-topbar">
      <div class="layout-topbar-logo-container">
        <button class="layout-menu-button layout-topbar-action" (click)="layoutService.onMenuToggle()" aria-label="منو">
          <i class="pi pi-bars"></i>
        </button>

        <a class="layout-topbar-logo" routerLink="/">
          @if (branding.logoUrl(); as logo) {
            <img [src]="logo" alt="" class="topbar-brand-logo" />
          }
          <span class="topbar-brand-name" [class.has-logo]="!!branding.logoUrl()">{{ branding.appName() }}</span>
        </a>
      </div>

      <div class="layout-topbar-actions">
        <!-- ساعت و تاریخ دقیق -->
        <div class="topbar-clock hidden md:flex">
          <span class="time">{{ clock() }}</span>
          <span class="date">{{ today() }}</span>
        </div>

        <!-- فیلتر لوکیشن (وقتی بیش از یک لوکیشن تعریف شده) -->
        @if (locations.hasMany()) {
          <select class="loc-filter" [ngModel]="locations.selected()" (ngModelChange)="selectLocation($event)" aria-label="لوکیشن">
            <option [ngValue]="null">همه‌ی لوکیشن‌ها</option>
            @for (l of locations.locations(); track l.id) {
              <option [ngValue]="l.id">{{ l.name }}</option>
            }
          </select>
        }

        @if (canSeeAlerts) {
          <app-notification-bell />
        }

        <!-- نام کاربر (سطح دسترسی)؛ پروفایل، تنظیمات نمایش و خروج در منوی زیر آن -->
        @if (user$ | async; as user) {
          <button type="button" class="user-trigger" (click)="openMenu($event, userMenu)" aria-haspopup="true">
            <span class="avatar">{{ initial(user) }}</span>
            <span class="name">{{ displayName(user) }}</span>
            <span class="role">({{ user.accessTitle || (user.roleId == 1 ? 'مدیر' : 'پرسنل') }})</span>
            <i class="pi pi-angle-down text-xs"></i>
          </button>
        }
        <p-popover #userMenu>
          <div class="user-menu">
            <a routerLink="/profile" class="user-menu-item" (click)="userMenu.hide()"><i class="pi pi-user"></i>پروفایل</a>
            <button type="button" class="user-menu-item" (click)="toggleDarkMode()">
              <i class="pi" [ngClass]="layoutService.isDarkTheme() ? 'pi-sun' : 'pi-moon'"></i>
              {{ layoutService.isDarkTheme() ? 'حالت روشن' : 'حالت تیره' }}
            </button>
            <button type="button" class="user-menu-item" (click)="showPrefs = !showPrefs">
              <i class="pi pi-palette"></i>تنظیمات نمایش
              <i class="pi mr-auto text-xs" [ngClass]="showPrefs ? 'pi-angle-up' : 'pi-angle-down'"></i>
            </button>
            @if (showPrefs && prefs) {
              <div class="display-prefs">
                <div>
                  <label class="block text-sm mb-2">حالت نمایش</label>
                  <p-selectbutton [options]="themeModes" optionLabel="label" optionValue="value" [(ngModel)]="prefs.themeMode" (ngModelChange)="savePrefs()" [allowEmpty]="false" size="small" />
                </div>
                <div>
                  <label class="block text-sm mb-2">طرح</label>
                  <p-selectbutton [options]="presets" [(ngModel)]="prefs.preset" (ngModelChange)="savePrefs()" [allowEmpty]="false" size="small" />
                </div>
                <div>
                  <label class="block text-sm mb-2">ساختار منو</label>
                  <p-selectbutton [options]="menuModes" optionLabel="label" optionValue="value" [(ngModel)]="prefs.menuMode" (ngModelChange)="savePrefs()" [allowEmpty]="false" size="small" />
                </div>
                <div class="flex items-center justify-between">
                  <label for="prefInfo" class="text-sm">آیکن راهنمای صفحات</label>
                  <p-toggleswitch inputId="prefInfo" [ngModel]="pageInfoVisible()" (ngModelChange)="setPageInfoVisible($event)" />
                </div>
                <small class="text-surface-500">رنگ‌های سازمانی توسط مدیر سامانه تعیین می‌شوند.</small>
                <div class="flex justify-between items-center">
                  <p-button label="پیش‌فرض سامانه" icon="pi pi-undo" size="small" text (onClick)="resetPrefs()" />
                  @if (canEditAppearance) {
                    <a routerLink="/appearance" class="text-primary text-sm" (click)="userMenu.hide()">ظاهر و رنگ‌بندی</a>
                  }
                </div>
              </div>
            }
            <a routerLink="/help" class="user-menu-item" (click)="userMenu.hide()"><i class="pi pi-question-circle"></i>راهنمای سامانه</a>
            <div class="menu-sep"></div>
            <button type="button" class="user-menu-item danger" (click)="userMenu.hide(); logout()"><i class="pi pi-sign-out"></i>خروج از سامانه</button>
          </div>
        </p-popover>
      </div>
    </div>
  `,
})
export class AppTopbar implements OnInit, OnDestroy {
  user$: Observable<CurrentUser | null>;
  canSeeAlerts: boolean;
  canEditAppearance: boolean;
  prefs: PanelTheme | null = null;
  showPrefs = false;
  readonly pageInfoVisible = pageInfoVisible;
  readonly setPageInfoVisible = setPageInfoVisible;

  // ساعت و تاریخ زنده، مثل «۱۴:۲۲:۰۱  چهارشنبه ۱۲ مهر ۱۴۰۵»
  clock = signal(clockFormatter.format(new Date()));
  today = signal(dateFormatter.format(new Date()));
  private timer?: ReturnType<typeof setInterval>;

  presets = ['Aura', 'Lara', 'Nora'];
  menuModes = [
    { label: 'ثابت', value: 'static' },
    { label: 'کشویی', value: 'overlay' },
  ];
  themeModes = [
    { label: 'روشن', value: 'light' },
    { label: 'تیره', value: 'dark' },
    { label: 'سیستم', value: 'system' },
  ];

  constructor(
    public layoutService: LayoutService,
    public branding: BrandingService,
    private auth: AuthService,
    private router: Router,
    public locations: LocationsService,
    private live: LiveRefreshService,
  ) {
    this.user$ = this.auth.user$;
    this.canSeeAlerts = this.auth.can('reservations.view');
    this.canEditAppearance = this.auth.can('appearance.manage');
  }

  ngOnInit(): void {
    this.timer = setInterval(() => {
      const now = new Date();
      this.clock.set(clockFormatter.format(now));
      this.today.set(dateFormatter.format(now));
    }, 1000);
    // اگر توکن نیست یا نامعتبره -> برو لاگین
    if (!this.auth.isTokenValid()) {
      this.auth.logout();
      this.router.navigate(['/auth/login']);
      return;
    }
    this.auth.loadMe().subscribe();
    this.locations.load().subscribe({ error: () => undefined });
  }

  selectLocation(id: number | null) {
    this.locations.select(id);
    // صفحه‌ی باز با لوکیشن جدید دوباره خوانده می‌شود
    this.live.refreshNow();
  }

  ngOnDestroy(): void {
    if (this.timer) clearInterval(this.timer);
  }

  displayName(user: CurrentUser) {
    return user.fullName || `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.username;
  }

  initial(user: CurrentUser) {
    return this.displayName(user).charAt(0);
  }

  openMenu(event: Event, panel: { toggle: (e: Event) => void }) {
    const b = this.branding.branding();
    if (b) this.prefs = { ...this.branding.effectivePanel(b) };
    panel.toggle(event);
  }

  savePrefs() {
    if (this.prefs) this.branding.savePanelPrefs({ ...this.prefs });
  }

  resetPrefs() {
    this.branding.resetPanelPrefs();
    const b = this.branding.branding();
    if (b) this.prefs = { ...b.panel };
  }

  toggleDarkMode() {
    const dark = !this.layoutService.isDarkTheme();
    this.branding.savePanelPrefs({ themeMode: dark ? 'dark' : 'light', darkMode: dark });
    if (this.prefs) this.prefs.themeMode = dark ? 'dark' : 'light';
  }

  logout() {
    this.auth.logout();
    this.router.navigate(['/auth/login'], { replaceUrl: true });
  }
}
